import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { motion, AnimatePresence, Reorder, useReducedMotion } from 'framer-motion';
import ObliqueIcon from '@/components/icons/ObliqueIcon';
import BreadcrumbNav from '@/components/shared/BreadcrumbNav';
import Export from '@/modules/Administration/components/settings/Export';
import Import from '@/modules/Administration/components/settings/Import';
import FilterTagsBar from '@/components/shared/FilterTagsBar';
import { useCompany } from '@/contexts/CompanyContext';
import CreateAttributeModal from '@/modules/Administration/components/settings/CreateAttributeModal';
import { ATTRIBUTE_ICONS } from '@/modules/CRM/components/CRMRecordSidebar';
import { getFiltersForPage } from '@/config/FilterRegistry';
import AutomationBuilder from '@/modules/Orbit/components/AutomationBuilder';
import FilterPanel from '@/components/filters/FilterPanel';
import { getFieldIcon } from '@/components/filters/FilterRuleRow';
import SortManager from '@/components/SortManager';
import { SettingsPanel, NewOptionsPanel, AutomationPanel, SharePanel, MoreMenu, MoreMenuItem, MoreMenuDivider, PageSettings, SavedViewsDropdown, SavedViewsTabs } from '@/core/oblique-ui';
// ─── Shared Panel Animation Config ───────────────────────────────────────────
// All five toolbar popovers (Filter, Sort, Automation, Settings, In-Page)
// use the SAME variants + transition so they feel like one system.
const PANEL_VARIANTS = {
  initial: {
    opacity: 0,
    y: 6,
    scale: 0.97
  },
  animate: {
    opacity: 1,
    y: 0,
    scale: 1
  },
  exit: {
    opacity: 0,
    y: 4,
    scale: 0.97
  }
};
const PANEL_TRANSITION = {
  duration: 0.15,
  ease: [0.16, 1, 0.3, 1]
};
const REDUCED_TRANSITION = {
  duration: 0.01
}; // respects prefers-reduced-motion
// ─────────────────────────────────────────────────────────────────────────────

const ActionToolbar = ({
  title = "",
  searchQuery,
  onSearchChange,
  filterTree = {
    id: 'root',
    type: 'group',
    operator: 'AND',
    children: []
  },
  onFilterTreeChange,
  // Legacy flat-filter support
  filterRules,
  filters,
  onFilterChange,
  onFilterRulesChange,
  sortRules = [],
  onSortChange,
  dateRange = {
    start: null,
    end: null
  },
  onDateRangeChange,
  onCommand,
  showTiles = true,
  onShowTilesChange,
  metricsConfig,
  viewSettings = {},
  onViewSettingsChange,
  onCreateNew,
  createNewLabel = 'New',
  properties = [],
  viewState,
  setFullViewState,
  isAdmin = false,
  onDatePropertyChange,
  onImport,
  onExport,
  onReport,
  onHubspotFetch,
  onRefresh,
  onAutomate,
  automationActions = [],
  onAssign,
  onCleanup,
  bottomTabs,
  pageActions = [],
  isDrawerOpen = false,
  isSyncing = false,
  titleComplement,
  customSearch,
  pageId = 'default',
  activeObject,
  onObjectChange,
  objectOptions = [],
  titleOptions = [],
  onTitleChange,
  activeTitleValue,
  viewMode = 'list',
  onViewModeChange,
  views = [],
  activeViewId,
  onViewChange,
  columns = [],
  hiddenColumns = [],
  onToggleColumn,
  breadcrumbs = [],
  showShareAndLink = true,
  onShare,
  onCopyLink,
  shareCopyMode,
  onCopyTable,
  onCopyText,
  onCopySlack,
  onSendEmail,
  customSharePanel,
  customFilterPanel,
  customFilterCount,
  showViewTabs = true,
  children
}) => {
  const {
    currentCompany
  } = useCompany();

  // Support flat filter arrays under a virtual root AND tree
  const activeFlatRules = filterRules || filters || [];
  const isLegacyFlat = !onFilterTreeChange && (onFilterChange || onFilterRulesChange);
  const effectiveFilterTree = isLegacyFlat ? {
    id: 'root',
    type: 'group',
    operator: 'AND',
    children: activeFlatRules
  } : filterTree || {
    id: 'root',
    type: 'group',
    operator: 'AND',
    children: []
  };
  const handleFilterTreeChange = useCallback(newTree => {
    if (isLegacyFlat) {
      const flatRules = newTree?.children || [];
      if (onFilterChange) onFilterChange(flatRules);else if (onFilterRulesChange) onFilterRulesChange(flatRules);
    } else if (onFilterTreeChange) {
      onFilterTreeChange(newTree);
    }
  }, [isLegacyFlat, onFilterChange, onFilterRulesChange, onFilterTreeChange]);
  const [isSearchExpanded, setIsSearchExpanded] = useState(!!searchQuery);
  const [activePanel, setActivePanel] = useState(null);
  const [showExportModal, setShowExportModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [showAutomationBuilder, setShowAutomationBuilder] = useState(false);
  const [shareTab, setShareTab] = useState('share');
  const [isFavorite, setIsFavorite] = useState(false);
  const [localActiveViewId, setLocalActiveViewId] = useState(activeViewId || null);
  useEffect(() => {
    if (activeViewId !== undefined) setLocalActiveViewId(activeViewId);
  }, [activeViewId]);
  const effectiveActiveViewId = activeViewId !== undefined ? activeViewId : localActiveViewId;

  // Check favorite status on mount and path change
  useEffect(() => {
    const checkFavorite = () => {
      const favs = JSON.parse(localStorage.getItem('oblique_favorites') || '[]');
      setIsFavorite(favs.some(f => f.path === window.location.pathname));
    };
    checkFavorite();
    window.addEventListener('oblique-favorites-updated', checkFavorite);
    return () => window.removeEventListener('oblique-favorites-updated', checkFavorite);
  }, [window.location.pathname]);
  const toggleFavorite = () => {
    const favs = JSON.parse(localStorage.getItem('oblique_favorites') || '[]');
    const currentPath = window.location.pathname;
    let newFavs;
    if (isFavorite) {
      newFavs = favs.filter(f => f.path !== currentPath);
    } else {
      newFavs = [...favs, {
        path: currentPath,
        title: titleString || 'Page'
      }];
    }
    localStorage.setItem('oblique_favorites', JSON.stringify(newFavs));
    setIsFavorite(!isFavorite);
    window.dispatchEvent(new CustomEvent('oblique-favorites-updated'));
  };
  const toolbarRef = useRef(null);
  // Respect OS "reduce motion" accessibility preference
  const prefersReducedMotion = useReducedMotion();
  const panelTransition = prefersReducedMotion ? REDUCED_TRANSITION : PANEL_TRANSITION;

  // Close panels on outside click
  useEffect(() => {
    const handleClickOutside = event => {
      if (toolbarRef.current && !toolbarRef.current.contains(event.target)) {
        setActivePanel(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  const togglePanel = panel => {
    setActivePanel(activePanel === panel ? null : panel);
  };
  const filterCount = customFilterCount !== undefined
    ? customFilterCount
    : (effectiveFilterTree?.children?.length || 0) + (dateRange?.start || dateRange?.end ? 1 : 0);
  const hasAutomations = onAutomate || automationActions && automationActions.length > 0;
  const titleString = useMemo(() => {
    if (typeof title === 'string') return title;
    if (!title) return 'Leads';
    try {
      if (title.props && title.props.children) {
        const children = title.props.children;
        if (typeof children === 'string') return children;
        if (Array.isArray(children)) {
          for (const child of children) {
            if (typeof child === 'string') return child;
            if (child && child.props && typeof child.props.children === 'string') {
              return child.props.children;
            }
          }
        }
        if (children && children.props && typeof children.props.children === 'string') {
          return children.props.children;
        }
      }
    } catch (e) {
      // fallback
    }
    return 'Leads';
  }, [title]);
  const targetCollectionName = useMemo(() => {
    return titleString.toLowerCase().replace(/\s+/g, '_');
  }, [titleString]);
  return <div ref={toolbarRef} className={`view-toolbar-container w-full flex flex-col bg-[var(--bg-surface)] [top:0] sticky ${activePanel ? 'z-[1000]' : 'z-[500]'}`}>
            {/* Breadcrumbs Row */}
            {(breadcrumbs?.length > 0 || titleComplement) && <div className={`relative ${activePanel === 'share-menu' ? 'z-[3000]' : 'z-[2500]'} flex items-center justify-between text-xs text-[var(--text-tertiary)] font-medium ${isDrawerOpen ? 'pt-3 px-4 pb-0' : 'pt-3 px-5 pb-0'}`}>
                    <BreadcrumbNav items={breadcrumbs} />
                    
                    <div className="shrink-0 flex items-center gap-1">
                        {showShareAndLink && <>
                                <button onClick={toggleFavorite} className={`hover-bg-subtle flex items-center justify-center w-[28px] h-[28px] rounded-md bg-transparent border-none cursor-pointer transition-colors ${isFavorite ? 'text-amber-500' : 'text-[var(--text-tertiary)]'}`} title={isFavorite ? "Remove from Favorites" : "Add to Favorites"}>
                                    <ObliqueIcon name={isFavorite ? 'star' : 'star_border'} size={18} />
                                </button>
                                <div className="relative z-[3000]">
                                <button onClick={() => setActivePanel(activePanel === 'share-menu' ? null : 'share-menu')} className={`hover-bg-subtle flex items-center gap-1.5 py-1 px-3 rounded-md border-none text-xs font-normal cursor-pointer transition-colors font-['Lexend_Deca',sans-serif] tracking-[-0.02em] ${activePanel === 'share-menu' ? 'bg-[var(--bg-subtle)] text-[var(--text-primary)]' : 'bg-transparent text-[var(--text-tertiary)]'}`}>
                                    <ObliqueIcon name="lock" size={14} />
                                    <span>Share</span>
                                    <ObliqueIcon name="expand_more" size={14} className="text-[var(--text-tertiary)] ml-[-2px]" />
                                </button>
                                
                                <AnimatePresence>
                                    {activePanel === 'share-menu' && (
                                        customSharePanel 
                                            ? customSharePanel({ onClose: () => setActivePanel(null) }) 
                                            : <SharePanel 
                                                titleString={titleString} 
                                                onCopyLink={onCopyLink} 
                                                onClose={() => setActivePanel(null)}
                                                copyMode={shareCopyMode}
                                                onCopyTable={onCopyTable}
                                                onCopyText={onCopyText}
                                                onCopySlack={onCopySlack}
                                                onExport={onExport}
                                                onSendEmail={onSendEmail}
                                              />
                                    )}
                                </AnimatePresence>
                            </div>
                            </>}
                        
                        <div className="relative flex">
                            <MoreMenu align="right">
                                <div className="p-1 flex items-center gap-2 border-b border-[var(--border)] mb-1">
                                    <ObliqueIcon name="search" size={16} color="var(--text-tertiary)" className="ml-2" />
                                    <input type="text" placeholder="Search actions..." className="border-none bg-transparent [outline:none] text-[13px] w-full py-1.5 px-0" />
                                </div>
                                <MoreMenuItem icon="grid_view" label="Layout" onClick={() => window.dispatchEvent(new CustomEvent('oblique-customize-layout'))} />
                                <MoreMenuItem 
                                    icon="vertical_align_bottom" 
                                    label="Export view" 
                                    onClick={() => {
                                        if (onExport) {
                                            onExport();
                                        } else {
                                            setShowExportModal(true);
                                        }
                                    }}
                                />
                                <MoreMenuItem icon="bar_chart" label="Reports" />
                                <MoreMenuItem icon="dns" label="Manage data sources" />
                                <MoreMenuItem icon="lock" label="Lock database" />
                                <MoreMenuItem icon="event" label="Manage in Calendar" />
                                <MoreMenuItem icon="delete" label="Move to Trash" color="var(--danger)" />
                                <MoreMenuDivider />
                                <div className="[padding:8px_12px_4px] text-[11px] text-[var(--text-tertiary)] [line-height:1.4]">
                                    Last edited by Sanjay Dev<br />
                                    May 28, 2026, 8:43 PM
                                </div>
                                <div className="hover-color-primary [padding:4px_16px_8px] text-xs text-[var(--text-secondary)] flex items-center gap-1.5 cursor-pointer">
                                    <ObliqueIcon name="help_outline" size={14} /> Learn about databases
                                </div>
                            </MoreMenu>
                        </div>
                    </div>
                </div>}

            {/* Main Toolbar Row (Title + Actions) */}
            <div className={`flex justify-between items-center w-full border-b border-[var(--border)] ${isDrawerOpen ? 'py-3 px-4' : 'py-4 px-5'}`}>
                {/* Left: Title */}
                <div className="flex items-center gap-3">
                    {titleOptions.length > 0 ? <div className="relative">
                            <motion.button whileHover={{
            background: 'var(--bg-subtle)'
          }} onClick={() => setActivePanel(activePanel === 'title-switcher' ? null : 'title-switcher')} className="flex items-center gap-[10px] bg-transparent border-none py-1 px-2 rounded-lg cursor-pointer text-2xl font-bold text-[var(--text-primary)] tracking-[-0.03em] m-0 [outline:none]">
                                {title}
                                <motion.div animate={{
              rotate: activePanel === 'title-switcher' ? 180 : 0
            }}>
                                    <ObliqueIcon name="expand_more" size={20} color="var(--text-tertiary)" />
                                </motion.div>
                            </motion.button>

                            <AnimatePresence>
                                {activePanel === 'title-switcher' && <motion.div initial={{
              opacity: 0,
              y: 8,
              scale: 0.95
            }} animate={{
              opacity: 1,
              y: 0,
              scale: 1
            }} exit={{
              opacity: 0,
              y: 8,
              scale: 0.95
            }} className="absolute [top:calc(100%_+_4px)] [left:0] w-[220px] bg-[var(--bg-surface)] border border-[var(--border)] rounded-xl shadow-[0_12px_32px_rgba(0,0,0,0.15)] z-[2000] p-1.5">
                                        {titleOptions.map(opt => <button key={opt.value} onClick={() => {
                onTitleChange?.(opt.value);
                setActivePanel(null);
              }} className={`hover-bg-muted w-full flex items-center gap-[10px] py-[10px] px-3 rounded-lg border-none text-sm cursor-pointer transition-colors text-left ${activeTitleValue === opt.value ? 'bg-[var(--bg-subtle)] text-[var(--accent)] font-bold' : 'bg-transparent text-[var(--text-secondary)] font-medium'}`}>
                                                {opt.icon && <ObliqueIcon name={opt.icon} size={18} />}
                                                {opt.label}
                                                {activeTitleValue === opt.value && <div className="ml-auto w-1.5 h-1.5 rounded-full bg-[var(--accent)]" />}
                                            </button>)}
                                    </motion.div>}
                            </AnimatePresence>
                        </div> : <div className="flex items-center gap-2.5 py-1 px-2">
                            <h1 className="text-2xl font-bold text-[var(--text-primary)] tracking-[-0.03em] m-0">
                                {title}
                            </h1>
                            {titleComplement && <div className="flex items-center">{titleComplement}</div>}
                        </div>}
                </div>

                {/* Right: Action Controls */}
                <div className={`flex items-center gap-1 relative ${(activePanel && activePanel !== 'share-menu') ? 'z-[2000]' : 'z-10'}`}>

                    {/* Saved Views Dropdown */}
                    {pageId ? <SavedViewsDropdown pageId={pageId} viewState={{
          activeViewId: effectiveActiveViewId,
          filters: isLegacyFlat ? activeFlatRules : effectiveFilterTree?.children,
          sorts: sortRules,
          viewMode: viewMode
        }} setFullViewState={state => {
          if (state.activeViewId !== undefined) {
            if (onViewChange) onViewChange(state.activeViewId);else setLocalActiveViewId(state.activeViewId);
          }
          if (state.filters !== undefined) {
            if (onFilterChange) onFilterChange(state.filters);else if (onFilterRulesChange) onFilterRulesChange(state.filters);else if (onFilterTreeChange) onFilterTreeChange({
              ...effectiveFilterTree,
              children: state.filters
            });
          }
          if (state.sorts !== undefined && onSortChange) onSortChange(state.sorts);
          if (state.viewMode !== undefined && onViewModeChange) onViewModeChange(state.viewMode);
        }} /> : <div className="flex items-center gap-2">
                            {views && views.length > 0 ? views.map(v => <button key={v.id} onClick={() => onViewChange?.(v.id)} className={`h-[28px] px-2.5 rounded-md text-[13px] font-medium transition-colors flex items-center gap-1.5 select-none ${activeViewId === v.id ? 'bg-[var(--bg-subtle)] text-[var(--text-primary)]' : 'bg-transparent text-[var(--text-secondary)] hover:bg-[var(--bg-muted)] hover:text-[var(--text-primary)]'}`}>
                                        {v.icon && <ObliqueIcon name={v.icon} size={15} color={activeViewId === v.id ? 'var(--text-primary)' : 'var(--text-secondary)'} />} 
                                        {v.label}
                                        {activeViewId === v.id && <ObliqueIcon name="expand_more" size={14} color="var(--text-tertiary)" />}
                                    </button>) : null}
                        </div>}
                    
                    {/* Divider */}
                    <div className={`w-[1px] h-4 bg-[var(--border)] my-0 mx-1.5 transition-opacity duration-150 ${isSearchExpanded ? 'opacity-0' : 'opacity-100'}`} />

                    {/* 1. Search */}
                    <div className="flex items-center">
                        {customSearch ? (
                            <div className="w-full max-w-[400px]">{customSearch}</div>
                        ) : (
                            <div 
                                style={{
                                    width: isSearchExpanded ? (isDrawerOpen ? 200 : 240) : 32,
                                    willChange: 'width'
                                }}
                                onClick={() => {
                                    if (!isSearchExpanded) {
                                        setIsSearchExpanded(true);
                                    }
                                }}
                                className={`h-[28px] rounded-md flex items-center overflow-hidden transition-[width,background-color,border-color] duration-180 ease-[cubic-bezier(0.16,1,0.3,1)] relative select-none ${
                                    isSearchExpanded 
                                        ? 'bg-[var(--bg-surface)] border border-[var(--border)] focus-within:border-[var(--accent)] focus-within:ring-2 focus-within:ring-[var(--accent-subtle)] px-2 gap-1.5 shadow-2xs' 
                                        : 'bg-transparent hover:bg-[var(--bg-subtle)] cursor-pointer justify-center text-[var(--text-tertiary)] hover:text-[var(--text-primary)]'
                                }`}
                                title={!isSearchExpanded ? "Search" : undefined}
                            >
                                <button 
                                    type="button"
                                    onClick={(e) => {
                                        if (isSearchExpanded && !searchQuery) {
                                            e.stopPropagation();
                                            setIsSearchExpanded(false);
                                        } else if (!isSearchExpanded) {
                                            setIsSearchExpanded(true);
                                        }
                                    }}
                                    className="!bg-transparent [background:transparent!important] border-none p-0 flex items-center justify-center cursor-pointer text-[var(--text-tertiary)] shrink-0"
                                >
                                    <ObliqueIcon name="search" size={16} />
                                </button>

                                <div className={`flex items-center flex-1 min-w-0 transition-opacity duration-150 ${isSearchExpanded ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}>
                                    <input 
                                        type="text" 
                                        placeholder="Search..." 
                                        value={searchQuery || ''} 
                                        onChange={e => onSearchChange?.(e.target.value)} 
                                        onKeyDown={e => {
                                            if (e.key === 'Escape') {
                                                onSearchChange?.('');
                                                setIsSearchExpanded(false);
                                            } else if (e.key === 'Enter' && searchQuery?.startsWith('/') && onCommand) {
                                                onCommand(searchQuery);
                                                onSearchChange?.('');
                                                setIsSearchExpanded(false);
                                            }
                                        }} 
                                        onBlur={(e) => {
                                            if (!searchQuery && !e.relatedTarget?.closest?.('.search-close-btn')) {
                                                setIsSearchExpanded(false);
                                            }
                                        }} 
                                        className="flex-1 !bg-transparent [background:transparent!important] !border-none !outline-none !shadow-none [border:none!important] [outline:none!important] [box-shadow:none!important] text-[13px] font-medium text-[var(--text-primary)] !p-0 [padding:0!important] !m-0 [margin:0!important] w-full" 
                                    />
                                    {searchQuery && (
                                        <button 
                                            type="button"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                onSearchChange?.('');
                                            }} 
                                            className="search-close-btn bg-transparent border-none p-0.5 cursor-pointer text-[var(--text-tertiary)] hover:text-[var(--text-primary)] flex items-center justify-center ml-1 shrink-0"
                                            title="Clear search"
                                        >
                                            <ObliqueIcon name="close" size={13} />
                                        </button>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>

                    {/* 2. Filter */}
                    <div className="relative">
                        <ToolbarButton 
                            icon={<ObliqueIcon name="filter_alt" size={20} weight={400} />} 
                            active={filterCount > 0} 
                            onClick={() => togglePanel('filter')} 
                            activePanel={activePanel === 'filter'} 
                            title="Filter" 
                        />
                        <AnimatePresence>
                            {activePanel === 'filter' && (
                                customFilterPanel 
                                    ? (typeof customFilterPanel === 'function' ? customFilterPanel({ onClose: () => setActivePanel(null) }) : customFilterPanel)
                                    : <FilterPanel schema={properties.length > 0 ? properties : getFiltersForPage(pageId).filters} tree={effectiveFilterTree} onChange={handleFilterTreeChange} onClose={() => setActivePanel(null)} />
                            )}
                        </AnimatePresence>
                    </div>

                    {/* 3. Sort */}
                    <div className="relative">
                        <ToolbarButton icon={<ObliqueIcon name="swap_vert" size={18} />} active={sortRules.filter(s => !s.hidden).length > 0} onClick={() => togglePanel('sort')} activePanel={activePanel === 'sort'} title="Sort" />
                        <AnimatePresence>
                            {activePanel === 'sort' && <SortManager properties={properties} currentSorts={sortRules} onSortChange={onSortChange} showTrigger={false} open={true} onClose={() => setActivePanel(null)} />}
                        </AnimatePresence>
                    </div>

                    {/* 4. Automations (only if callbacks exist) */}
                    {hasAutomations && <div className="relative">
                            <ToolbarButton icon={<ObliqueIcon name="flash_on" size={18} weight={500} />} onClick={() => {
            if (automationActions?.length > 0) togglePanel('automation');else if (onAutomate) onAutomate();
          }} activePanel={activePanel === 'automation'} title="Automations" />
                            <AnimatePresence>
                                {activePanel === 'automation' && <AutomationPanel actions={automationActions} onClose={() => setActivePanel(null)} onOpenBuilder={() => setShowAutomationBuilder(true)} />}
                            </AnimatePresence>
                        </div>}

                    {/* Inline Sync / Refresh Button */}
                    {onRefresh && <ToolbarButton icon={<motion.div animate={isSyncing ? {
          rotate: 360
        } : {}} transition={isSyncing ? {
          repeat: Infinity,
          duration: 1,
          ease: 'linear'
        } : {}} className="flex">
                                    <ObliqueIcon name="sync" size={18} />
                                </motion.div>} onClick={onRefresh} title="Sync / Refresh Data" />}

                    <div className="relative">
                        <ToolbarButton icon={<ObliqueIcon name="tune" size={18} />} onClick={() => togglePanel('settings')} activePanel={activePanel === 'settings'} title="Settings" />
                        <AnimatePresence>
                            {activePanel === 'settings' && <SettingsPanel onClose={() => setActivePanel(null)} onImport={onImport ? () => {
              setActivePanel(null);
              onImport();
            } : () => {
              setActivePanel(null);
              setShowImportModal(true);
            }} onExport={onExport ? () => {
              setActivePanel(null);
              onExport();
            } : null} onReport={onReport ? () => {
              setActivePanel(null);
              onReport();
            } : null} onRefresh={onRefresh} isSyncing={isSyncing} onCleanup={onCleanup} pageActions={pageActions} onHubspotFetch={onHubspotFetch} showTiles={showTiles} onShowTilesChange={onShowTilesChange} viewSettings={viewSettings} onViewSettingsChange={onViewSettingsChange} filterCount={filterCount} sortCount={sortRules?.length || 0} propertyCount={properties?.length || 0} onOpenFilter={() => setActivePanel('filter')} onOpenSort={() => setActivePanel('sort')} viewMode={viewMode} columns={columns.length ? columns : properties} hiddenColumns={hiddenColumns} onToggleColumn={onToggleColumn} onLayoutChange={mode => onViewModeChange && onViewModeChange(mode.toLowerCase())} metricsConfig={metricsConfig} activeObject={activeObject} objectOptions={objectOptions} onObjectChange={onObjectChange} />}
                        </AnimatePresence>
                    </div>

                    {/* 6. New Button with dropdown (Notion Style) */}
                    {(isAdmin || onCreateNew) && <div className="flex ml-2">
                            <button onClick={() => onCreateNew && onCreateNew('default')} className="h-[28px] py-0 px-3 bg-[#2383e2] hover:bg-[#1a6ec0] text-white border-none rounded-l text-[13px] font-medium cursor-pointer flex items-center justify-center transition-colors">
                                {createNewLabel}
                            </button>
                            <button onClick={() => togglePanel('new-options')} className="h-[28px] w-6 bg-[#2383e2] hover:bg-[#1a6ec0] text-white border-none border-l border-white/30 rounded-r flex items-center justify-center cursor-pointer transition-colors">
                                <ObliqueIcon name="expand_more" size={16} weight={500} className={`transition-transform duration-200 ${activePanel === 'new-options' ? 'rotate-180' : 'rotate-0'}`} />
                            </button>
                        </div>}

                    {/* Additional Children */}
                    {children && <div className="ml-2 flex items-center">{children}</div>}

                    {/* New Options Dropdown */}
                    {activePanel === 'new-options' && <NewOptionsPanel onCreateNew={onCreateNew} onImport={onImport ? () => {
                        setActivePanel(null);
                        onImport();
                    } : () => {
                        setActivePanel(null);
                        setShowImportModal(true);
                    }} onHubspotFetch={onHubspotFetch} onClose={() => setActivePanel(null)} />}
                </div>

                {/* Modals */}
                <Export isOpen={showExportModal} onClose={() => setShowExportModal(false)} targetCollection={targetCollectionName} companyId={currentCompany?.id} />
                <Import isOpen={showImportModal} onClose={() => setShowImportModal(false)} targetCollection={targetCollectionName} companyId={currentCompany?.id} />
            </div>

            {/* HubSpot-Style Horizontal Saved Views Tabs Strip (Only shown when explicitly requested) */}
            {pageId && showViewTabs === true && (
                <SavedViewsTabs
                    pageId={pageId}
                    defaultTitle={titleString ? `All ${titleString.toLowerCase()}` : 'All'}
                    viewState={{
                        activeViewId: effectiveActiveViewId,
                        filters: isLegacyFlat ? activeFlatRules : effectiveFilterTree?.children,
                        sorts: sortRules,
                        viewMode: viewMode
                    }}
                    setFullViewState={state => {
                        if (state.activeViewId !== undefined) {
                            if (onViewChange) onViewChange(state.activeViewId);
                            else setLocalActiveViewId(state.activeViewId);
                        }
                        if (state.filters !== undefined) {
                            if (onFilterChange) onFilterChange(state.filters);
                            else if (onFilterRulesChange) onFilterRulesChange(state.filters);
                            else if (onFilterTreeChange) onFilterTreeChange({
                                ...effectiveFilterTree,
                                children: state.filters
                            });
                        }
                        if (state.sorts !== undefined && onSortChange) onSortChange(state.sorts);
                        if (state.viewMode !== undefined && onViewModeChange) onViewModeChange(state.viewMode);
                    }}
                />
            )}

            {/* Row 3: Active Filters/Sorts tags */}
            <AnimatePresence>
                {((effectiveFilterTree?.children?.length || 0) > 0 || dateRange?.start || dateRange?.end || sortRules.length > 0) && <motion.div initial={{
        opacity: 0,
        height: 0
      }} animate={{
        opacity: 1,
        height: 'auto'
      }} exit={{
        opacity: 0,
        height: 0
      }} transition={{
        duration: 0.15,
        ease: 'easeOut'
      }} className={`flex items-center gap-3 overflow-hidden ${isDrawerOpen ? 'py-2.5 px-4' : 'py-2.5 px-5'}`}>
                        <FilterTagsBar rules={effectiveFilterTree?.children || []} onRemoveRule={idx => {
          const newChildren = (effectiveFilterTree?.children || []).filter((_, i) => i !== idx);
          handleFilterTreeChange({
            ...effectiveFilterTree,
            children: newChildren
          });
        }} onAddFilter={() => togglePanel('filter')} onPillClick={(rule, idx) => togglePanel('filter')} properties={properties} dateRange={dateRange} onRemoveDateRange={() => onDateRangeChange({
          start: null,
          end: null
        })} compact={false} sortRules={sortRules.filter(s => !s.hidden)} onRemoveSort={idx => {
          const visibleSorts = sortRules.filter(s => !s.hidden);
          const sortToRemove = visibleSorts[idx];
          onSortChange(sortRules.filter(s => s !== sortToRemove));
        }} />
                    </motion.div>}
            </AnimatePresence>

            {showAutomationBuilder && <AutomationBuilder isOpen={showAutomationBuilder} onClose={() => setShowAutomationBuilder(false)} properties={properties} activeDatabaseName={titleString} />}
            
            {/* Bottom Tabs (e.g. for BAU Audits) */}
            {bottomTabs && (
                <div className="w-full flex items-center bg-[var(--bg-surface)] px-5 py-2 border-b border-[var(--border)]">
                    {bottomTabs}
                </div>
            )}
        </div>;
};

// ─── Sub-Components ─────────────────────────────────────────

const ToolbarButton = ({
  icon,
  active,
  onClick,
  activePanel,
  title
}) => <motion.button whileHover={{
  scale: 1.05,
  backgroundColor: 'var(--bg-subtle)'
}} whileTap={{
  scale: 0.95
}} onClick={onClick} title={title} className={`h-[28px] w-8 min-w-8 flex items-center justify-center rounded-sm border-none cursor-pointer transition-all duration-150 relative shrink-0 ${active || activePanel ? 'bg-[var(--bg-subtle)] text-[#2383e2]' : 'bg-transparent text-[var(--text-tertiary)] hover:bg-[var(--bg-subtle)]'}`}>
        {React.isValidElement(icon) ? React.cloneElement(icon, {
    filled: active || activePanel,
    size: 18
  }) : icon}
    </motion.button>;

const ObliqueSwitch = ({
  checked,
  onChange
}) => <div onClick={e => {
  e.stopPropagation();
  onChange(!checked);
}} className={`w-8 h-[18px] rounded-2xl relative cursor-pointer transition-colors duration-200 shrink-0 ${checked ? 'bg-[#2383e2]' : 'bg-zinc-300'}`}>
        <div className={`w-[14px] h-[14px] rounded-full bg-white absolute top-[2px] transition-[left] duration-200 ease-out shadow-sm ${checked ? 'left-4' : 'left-0.5'}`} />
    </div>;

export default ActionToolbar;